import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel, MOBILE_PRODUCT_CARD_WIDTH } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useEffect } from 'react';

import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CustomLanguage } from '@jane/bloom/loaders/rootLoader';
import { getThemedProductCardWidth } from '@jane/bloom/utils/product/getThemedProductCardWidth';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';
import type { StoreSpecials } from '@jane/bloom/viewModels/schemas/storeSpecials';

import styles from './menuRow.module.css';

export type DMMenuRowProps = {
  brandSpecials: BrandSpecials;
  customLanguage: CustomLanguage;
  embedInProductGrid?: boolean;
  janeGold: JaneGold;
  menuTopRow: SmartSort<MaterializedMenuProduct>;
  mode?: CardCarouselProps['mode'];
  pauseRefetching: () => void;
  productSelected?: (product: ProductSearchResultViewModel) => void;
  specials: Nullable<StoreSpecials>;
  unpauseRefetching: () => void;
};

export const DMMenuRow = ({
  brandSpecials,
  customLanguage,
  embedInProductGrid,
  janeGold,
  menuTopRow,
  mode = 'min-width',
  pauseRefetching,
  productSelected,
  specials,
  unpauseRefetching,
}: DMMenuRowProps) => {
  const { cart, featureFlags, pinnedStore, theme } = useRootLoaderData();
  const cardWidth = getThemedProductCardWidth(theme);
  const { products, title } = menuTopRow;
  const visibleProductCount = products.length;

  useEffect(() => {
    if (menuTopRow) {
      menuTopRow.onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCardSelect = async (product: ProductSearchResultViewModel) => {
    productSelected?.(product);
  };

  useEffect(() => {
    products.forEach((product) => product.impress());
  }, [products]);

  if (!products.length) {
    return null;
  }

  return (
    <CardCarousel
      cardWidth={cardWidth}
      cardWidthMobile={MOBILE_PRODUCT_CARD_WIDTH}
      className={clsx('sponsoredRow', {
        customRow: !embedInProductGrid,
        [styles['topMenuRow']]: embedInProductGrid,
      })}
      data-testid="sponsored-row"
      mobileScrollPadding={16}
      mode={mode}
      name={title ?? ''}
      onMouseEnter={pauseRefetching}
      onMouseLeave={unpauseRefetching}
      subtitle={
        embedInProductGrid
          ? undefined
          : pluralize({ noun: 'product', number: visibleProductCount })
      }
      surtitle="Sponsored"
    >
      {products.map((product, ind) => {
        const builtProduct = buildSingleProductSearchResultViewModel({
          bundleTag: featureFlags[FLAGS.c1BundleTag],
          cart,
          customLanguage,
          janeGold,
          pinnedStore,
          product: product.attributes,
          specials,
        });
        return (
          <CardCarousel.Card key={product.objectId}>
            <ProductCard
              brandSpecials={brandSpecials}
              product={builtProduct}
              selected={() => {
                product.click();
                onCardSelect(builtProduct);
              }}
              preloaded={ind <= 5}
              smartProductInstance={product}
            />
          </CardCarousel.Card>
        );
      })}
    </CardCarousel>
  );
};
